import React, { useState } from 'react';
import { api } from '../App';
import { FileText, Download, Play, X, CheckCircle } from 'lucide-react';

const CATEGORIES = { hiring: '#2E7D32', separation: '#D32F2F', discipline: '#F9A825', legal: '#1565C0', policy: '#7B1FA2', handbook: '#00897B', performance: '#283593', compensation: '#E65100', advancement: '#AD1457' };

async function downloadDoc(content, title, format, agentName='Panzix HR AI Workforce') {
  try {
    const res = await api.post('/api/documents/generate', { content, title, format, agent_name: agentName });
    const { data, filename, content_type } = res.data;
    const byteChars = atob(data);
    const arr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([arr], { type: content_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { alert('Download failed'); }
}

export default function TemplateLibrary() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('all');

  React.useEffect(() => {
    api.get('/api/templates/library').then(r => { setTemplates(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleGenerate = async () => {
    setGenerating(true); setResult(null);
    try {
      const r = await api.post('/api/templates/generate', { template_id: selectedTemplate.id, variables });
      setResult(r.data);
    } catch (e) { setResult({ content: 'Generation failed' }); }
    finally { setGenerating(false); }
  };

  const categories = [...new Set(templates.map(t => t.category))];
  const filtered = filter === 'all' ? templates : templates.filter(t => t.category === filter);

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in" style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>HR Document Templates</h3>
        <p style={{ fontSize: 14, color: '#64748b' }}>Professional HR document templates powered by AI. Fill in the details and generate instantly.</p>
      </div>

      {/* Category Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={() => setFilter('all')} className={filter === 'all' ? 'btn-action' : 'btn-secondary'} style={{ padding: '6px 14px', fontSize: 12 }}>All ({templates.length})</button>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{ padding: '6px 14px', fontSize: 12, borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, background: filter === c ? `${CATEGORIES[c]}18` : '#f1f5f9', color: filter === c ? CATEGORIES[c] : '#64748b', textTransform: 'capitalize' }}>
            {c.replace('_', ' ')} ({templates.filter(t => t.category === c).length})
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {filtered.map(t => (
          <div key={t.id} onClick={() => { setSelectedTemplate(t); setVariables({}); setResult(null); }}
            style={{ background: 'white', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; }}
            data-testid={`template-${t.id}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${CATEGORIES[t.category] || '#64748b'}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={20} color={CATEGORIES[t.category] || '#64748b'} />
              </div>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700 }}>{t.name}</h4>
                <span style={{ fontSize: 11, color: CATEGORIES[t.category] || '#64748b', fontWeight: 600, textTransform: 'capitalize' }}>{t.category.replace('_', ' ')}</span>
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>{t.description}</p>
            <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>{t.fields.length} fields to fill</div>
          </div>
        ))}
      </div>

      {/* Generate Modal */}
      {selectedTemplate && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedTemplate(null)}>
          <div className="task-modal slide-up" style={{ maxWidth: 600, maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>{selectedTemplate.name}</h3>
              <button onClick={() => setSelectedTemplate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{selectedTemplate.description}</p>

            {!result ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {selectedTemplate.fields.map(f => (
                    <div key={f} className="form-group">
                      <label style={{ textTransform: 'capitalize' }}>{f.replace(/_/g, ' ')}</label>
                      <input value={variables[f] || ''} onChange={e => setVariables(v => ({...v, [f]: e.target.value}))} placeholder={`Enter ${f.replace(/_/g, ' ')}`} data-testid={`field-${f}`} />
                    </div>
                  ))}
                </div>
                <button className="btn-action" onClick={handleGenerate} disabled={generating} style={{ width: '100%', marginTop: 12 }} data-testid="generate-template">
                  <Play size={14} style={{ marginRight: 6 }} />{generating ? 'Generating...' : 'Generate Document'}
                </button>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><CheckCircle size={18} color="#22c55e" /><span style={{ fontWeight: 600, color: '#166534' }}>Document Generated!</span></div>
                <div className="task-result" style={{ maxHeight: 300 }}><pre style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{result.content}</pre></div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn-action" style={{ flex: 1, fontSize: 13 }} onClick={() => downloadDoc(result.content, result.template_name || 'Document', 'docx')}><Download size={14} style={{ marginRight: 4 }} />Word</button>
                  <button className="btn-action purple" style={{ flex: 1, fontSize: 13 }} onClick={() => downloadDoc(result.content, result.template_name || 'Document', 'pdf')}><Download size={14} style={{ marginRight: 4 }} />PDF</button>
                </div>
                <button className="btn-secondary" onClick={() => setResult(null)} style={{ width: '100%', marginTop: 8 }}>Generate Another</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
